import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  Spinner,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import { formatDateTimeForDisplay } from '../../utils/dateUtils';
import { useGetPatchNotes } from 'api/system/service';
import { getActiveColorStyle } from "utils/colorUtils";

const PatchNotes = ({ activeColor = "blue" }) => {
  const { data, isLoading, isError, error } = useGetPatchNotes();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [activeSection, setActiveSection] = useState("");
  const observer = useRef(null);

  // Helper to extract a clean version number for grouping, but keep original for display
  const getRootVersion = (versionStr) => {
    if (!versionStr) return "System";

    // Extract numbers like 0.1.6 from strings
    const match = versionStr.match(/\d+\.\d+(\.\d+)?/);
    if (match) return `v${match[0]}`;

    // If no numbers, return the first word or "System"
    const cleaned = versionStr.replace(/[\u2700-\u27bf]|[\u1f300-\u1f64f]|[\u1f680-\u1f6ff]/g, '').trim();
    return cleaned || "System";
  };

  const entries = useMemo(() => {
    if (!data) return [];
    const raw = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
    return [...raw].sort((a, b) => {
      const da = new Date(a?.createdAt).getTime();
      const db = new Date(b?.createdAt).getTime();
      return db - da;
    });
  }, [data]);

  const groupedEntries = useMemo(() => {
    const groups = {};
    entries.forEach(entry => {
      const rootV = getRootVersion(entry.version);
      if (!groups[rootV]) groups[rootV] = [];
      groups[rootV].push(entry);
    });
    return Object.entries(groups).sort((a, b) => {
      // Sort numerically by version extracted
      return b[0].localeCompare(a[0], undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [entries]);

  useEffect(() => {
    if (isLoading || groupedEntries.length === 0) return;

    const options = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0
    };

    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id.replace('section-', ''));
        }
      });
    }, options);

    groupedEntries.forEach(([rootKey]) => {
      const element = document.getElementById(`section-${rootKey}`);
      if (element) observer.current.observe(element);
    });

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [isLoading, groupedEntries]);

  const scrollToSection = (id) => {
    const element = document.getElementById(`section-${id}`);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  const renderMarks = (textNode, key) => {
    let content = textNode?.text ?? '';
    let element = <>{content}</>;
    if (textNode?.code) element = <code key={`code-${key}`} style={{ background: 'rgba(0,0,0,0.05)', color: activeColorStyle.borderColor, padding: '2px 6px', fontStyle: 'normal' }}>{element}</code>;
    if (textNode?.underline) element = <u key={`u-${key}`}>{element}</u>;
    if (textNode?.italic) element = <em key={`em-${key}`}>{element}</em>;
    if (textNode?.bold) element = <strong key={`strong-${key}`} style={{ fontWeight: '800' }}>{element}</strong>;
    return <React.Fragment key={key}>{element}</React.Fragment>;
  };

  const renderInline = (children = []) => {
    return (children || []).map((child, idx) => {
      if (child?.type === 'text' || typeof child?.text === 'string') {
        return renderMarks(child, idx);
      }
      if (child?.type === 'link' && child?.url) {
        return (
          <a
            key={`link-${idx}`}
            href={child.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-info"
            style={{ textDecoration: 'underline' }}
          >
            {renderInline(child.children)}
          </a>
        );
      }
      if (Array.isArray(child?.children)) {
        return <React.Fragment key={idx}>{renderInline(child.children)}</React.Fragment>;
      }
      return null;
    });
  };

  const renderNode = (node, idx) => {
    const type = node?.type || 'paragraph';
    if (type === 'paragraph') {
      return (
        <p key={idx} className="mb-2" style={{ whiteSpace: 'pre-wrap', fontSize: '15px', lineHeight: '1.7' }}>
          {renderInline(node?.children)}
        </p>
      );
    }
    if (type === 'list') {
      const isOrdered = node?.format === 'ordered';
      const Tag = isOrdered ? 'ol' : 'ul';
      return (
        <Tag key={idx} className="mb-3" style={{ paddingLeft: '1.5rem' }}>
          {(node?.children || []).map((li, liIdx) => renderNode(li, `${idx}-${liIdx}`))}
        </Tag>
      );
    }
    if (type === 'list-item') {
      return (
        <li key={idx} className="mb-2" style={{ fontSize: '14.5px' }}>
          {renderInline(node?.children)}
        </li>
      );
    }
    return <p key={idx}>{renderInline(node?.children)}</p>;
  };

  const renderRichText = (nodes) => {
    if (!Array.isArray(nodes)) return null;
    return nodes.map((n, i) => renderNode(n, i));
  };

  return (
    <div className="content">
      <Row>
        <Col md="9">
          <div className="mb-5 ml-2">
            <h2 className="title font-weight-bold" style={{ fontSize: '1.75rem' }}>Patch Notes</h2>
            <div style={{ width: '60px', height: '4px', background: activeColorStyle.borderColor }} />
          </div>

          {isLoading && (
            <div className="text-center p-5">
              <Spinner color="primary" />
              <p className="mt-3 opacity-50">Fetching updates...</p>
            </div>
          )}

          {isError && (
            <div className="alert alert-danger mx-2">
              <i className="tim-icons icon-alert-circle-exc mr-2" />
              {error?.message || 'Error loading records.'}
            </div>
          )}

          {!isLoading && !isError && groupedEntries.map(([rootKey, notes]) => (
            <div key={rootKey} id={`section-${rootKey}`} className="mb-5 px-2">
              <div className="d-flex align-items-center mb-4">
                <div style={{
                  padding: '8px',
                  background: `${activeColorStyle.borderColor}15`,
                  marginRight: '15px'
                }}>
                  <i className="tim-icons icon-tag" style={{ color: activeColorStyle.borderColor }} />
                </div>
                <h3 className="mb-0 font-weight-bold" style={{ fontSize: '20px' }}>
                  Version {rootKey}
                </h3>
                <div className="flex-grow-1 ml-4" style={{ height: '1px', background: 'rgba(0,0,0,0.05)' }} />
              </div>

              <div className="d-flex flex-column">
                {notes.map((note) => (
                  <Card key={note.id} className="mb-4 shadow-sm border-0">
                    <CardBody className="p-4">
                      <div className="mb-4">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="d-flex align-items-center">
                            <span className="badge badge-primary px-3 py-2" style={{
                              background: activeColorStyle.borderColor,
                              fontSize: '11px',
                              textTransform: 'uppercase'
                            }}>
                              {note.version}
                            </span>
                            <h4 className="card-title mb-0 font-weight-bold ml-2" style={{ fontSize: '18px' }}>
                              {note.title || `Update ${note.version}`}
                            </h4>
                          </div>
                          <small className="text-muted font-weight-bold">
                            <i className="tim-icons icon-watch-time mr-1" />
                            {formatDateTimeForDisplay(note.createdAt)}
                          </small>
                        </div>
                      </div>

                      <div className="patch-content">
                        {note.description && (
                          <div className="mb-4" style={{ fontSize: '15.5px', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
                            {note.description}
                          </div>
                        )}

                        {Array.isArray(note.items) && note.items.length > 0 && (
                          <ul className="mb-0 pl-4" style={{ fontSize: '15px' }}>
                            {note.items.map((item, i) => (
                              <li key={i} className="mb-2">{item}</li>
                            ))}
                          </ul>
                        )}

                        {Array.isArray(note.notes) && note.notes.length > 0 && (
                          <div className="mt-4">
                            {renderRichText(note.notes)}
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </Col>

        <Col md="3">
          <div className="sticky-top" style={{ top: '100px', paddingLeft: '10px' }}>
            <div className="mb-3 px-3">
              <h6 className="opacity-50 text-uppercase mb-0 font-weight-bold" style={{ fontSize: '11px', letterSpacing: '2px' }}>
                Timeline
              </h6>
            </div>
            <Nav vertical className="nav-pills-primary">
              {groupedEntries.map(([rootKey]) => (
                <NavItem key={rootKey}>
                  <NavLink
                    href="#"
                    onClick={(e) => { e.preventDefault(); scrollToSection(rootKey); }}
                    className={`nav-link ${activeSection === rootKey ? 'active' : ''}`}
                    style={{
                      background: activeSection === rootKey ? activeColorStyle.borderColor : 'transparent',
                      color: activeSection === rootKey ? '#fff' : 'inherit',
                      marginBottom: '5px',
                      transition: '0.3s all',
                      fontSize: '13.5px',
                      fontWeight: '600',
                      padding: '10px 15px',
                      boxShadow: activeSection === rootKey ? `0 4px 15px ${activeColorStyle.borderColor}66` : 'none'
                    }}
                  >
                    Version {rootKey}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </div>
        </Col>
      </Row>
      <style>{`
        .nav-link:hover:not(.active) {
          background: rgba(0,0,0,0.05) !important;
          transform: translateX(5px);
        }
      `}</style>
    </div>
  );
};

export default PatchNotes;
